import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { captureException } from '@sentry/react';
import { signInWithClient, useUserClientContext } from '@/app/clientContext';

/**
 * Mirrors `?client=CODE` into the active client context for users who can
 * switch clients (internal superusers and multi-tenant externals). Users
 * without switching access are ignored, as is any `?client=` accompanying a
 * `?delivery=` — the delivery determines its own client, so honoring the param
 * would fight that resolution (and could try to form `internal@<CODE>` for a
 * client the delivery doesn't belong to).
 */
export const useSyncClientFromQueryParam = () => {
  const [searchParams] = useSearchParams();
  const queryClientCode = searchParams.get('client')?.toUpperCase();
  const hasDelivery = searchParams.get('delivery') !== null;
  const {
    isUserLoaded,
    canClientSwitch,
    email,
    context,
    hydrateUserAndClientContext,
  } = useUserClientContext();
  const lastSwitchedCode = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!isUserLoaded) return;
    if (!canClientSwitch) return;
    if (hasDelivery) return;
    if (!queryClientCode) return;
    if (queryClientCode === context.code?.toUpperCase()) return;
    if (queryClientCode === lastSwitchedCode.current) return;

    const attempted = queryClientCode;
    lastSwitchedCode.current = attempted;
    signInWithClient(hydrateUserAndClientContext, email, attempted).catch(
      (error) => {
        captureException(error);
        // Clear the guard so a retry (e.g. the user fixing a typo'd code or
        // a transient network blip) can proceed; without this a single
        // failure permanently silences the hook for that value.
        if (lastSwitchedCode.current === attempted) {
          lastSwitchedCode.current = undefined;
        }
      }
    );
  }, [
    isUserLoaded,
    canClientSwitch,
    hasDelivery,
    queryClientCode,
    context.code,
    email,
    hydrateUserAndClientContext,
  ]);
};
